import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Field } from "../components/Shared";
import { SignaturePad } from "../components/SignaturePad";
import { api, fmtDate, formatApiError } from "../lib/api";

const TEXTS = {
  depart: "Je reconnais que l'entreprise a pris en charge mes biens conformément à l'inventaire ci-dessous et dans l'état constaté au chargement.",
  arrivee: "Je reconnais avoir reçu mes biens à l'adresse de livraison. Je peux indiquer ci-dessous mes éventuelles réserves (casse, manquants).",
};

function DocumentSummary({ d }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="remote-sign-document">
      <p className="text-xs font-semibold uppercase tracking-widest text-teal-600">{d.company?.name}</p>
      <h1 className="mt-1 text-2xl font-bold text-slate-900">{d.document}</h1>
      <p className="mt-1 text-sm text-slate-500">Client : {d.move.client_name} · {fmtDate(d.move.date)} · {d.move.volume_m3} m³{d.quote_number && ` · Devis ${d.quote_number}`}</p>
      <div className="mt-4 space-y-1 text-sm text-slate-700">
        <p className="flex gap-2"><MapPin className="h-4 w-4 text-teal-500" /><b>Départ :</b> {d.move.origin || "—"}</p>
        <p className="flex gap-2"><MapPin className="h-4 w-4 text-teal-500" /><b>Arrivée :</b> {d.move.destination || "—"}</p>
      </div>
      {d.items.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead><tr className="border-b text-left text-xs uppercase text-slate-400"><th className="py-1.5">Inventaire</th><th className="text-right">Qté</th></tr></thead>
          <tbody>{d.items.map((it, i) => <tr key={i} className="border-b border-slate-100"><td className="py-1">{it.name}</td><td className="text-right">{it.quantity}</td></tr>)}</tbody>
        </table>
      )}
      {d.kind === "arrivee" && d.incidents && <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Incidents notés par l'équipe : {d.incidents}</p>}
      <p className="mt-4 text-xs text-slate-500">{d.company?.address} {d.company?.siret && `· SIRET ${d.company.siret}`} {d.company?.phone && `· ${d.company.phone}`}</p>
    </div>
  );
}

function SignForm({ token, d, onDone }) {
  const pad = useRef(null);
  const [name, setName] = useState(d.move.client_name || "");
  const [reserves, setReserves] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    if (pad.current.isEmpty()) return setError("Merci de signer dans le cadre.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/public/signatures/${token}`, { signer_name: name, image_data: pad.current.toDataURL(), reserves, accept });
      onDone(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 space-y-4 rounded-2xl border border-teal-200 bg-white p-6" data-testid="remote-sign-form">
      <p className="text-sm text-slate-700">{TEXTS[d.kind]}</p>
      {d.kind === "arrivee" && (
        <Field label="Réserves éventuelles"><Textarea data-testid="remote-sign-reserves-input" rows={3} value={reserves} onChange={(e) => setReserves(e.target.value)} placeholder="Aucune réserve" /></Field>
      )}
      <Field label="Nom et prénom du signataire"><Input data-testid="remote-sign-name-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <SignaturePad ref={pad} />
      <label className="flex items-start gap-2 text-sm text-slate-600">
        <Checkbox data-testid="remote-sign-accept-checkbox" checked={accept} onCheckedChange={(v) => setAccept(!!v)} className="mt-0.5" />
        Je certifie l'exactitude des informations et signe électroniquement ce document.
      </label>
      {d.kind === "arrivee" && <p className="text-xs text-slate-400">Vous disposez de 10 jours à compter de la livraison pour adresser une protestation motivée au déménageur (art. L224-63 du Code de la consommation).</p>}
      {error && <p className="text-sm text-red-600" data-testid="remote-sign-error">{error}</p>}
      <Button data-testid="remote-sign-submit-button" disabled={busy || !accept || name.trim().length < 2} onClick={submit} className="w-full rounded-full bg-[#0b2c6b] py-6 text-base hover:bg-[#1559d6]">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Signer le document
      </Button>
    </div>
  );
}

export default function RemoteSign() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/public/signatures/${token}`).then((r) => setD(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  if (error) return <div className="flex min-h-screen items-center justify-center p-6 text-slate-600" data-testid="remote-sign-page-error">{error}</div>;
  if (!d) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1559d6]" /></div>;
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <DocumentSummary d={d} />
        {d.status === "signed" ? (
          <div className="mt-4 rounded-2xl bg-teal-50 p-5 text-sm font-semibold text-teal-800" data-testid="remote-sign-done">
            <CheckCircle2 className="mr-2 inline h-5 w-5" />Document signé par {d.signer_name} le {fmtDate(d.signed_at)}. Merci !
            {d.reserves && <p className="mt-2 font-normal">Réserves : {d.reserves}</p>}
          </div>
        ) : <SignForm token={token} d={d} onDone={setD} />}
        <p className="mt-8 text-center text-xs text-slate-400">Signature électronique sécurisée par Logidem</p>
      </div>
    </div>
  );
}
